import React from 'react';
import { useNavigate } from 'react-router-dom';
import Breadcrumbs from 'components/Breadcrumbs';
import { useTranslation } from 'react-i18next';

const SafetyPro = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navigateToTool = (tool) => {
    const tools = {
      inertization: '/tools/safety-pro/inertization',
      ventilation: '/tools/safety-pro/ventilation',
    };
    navigate(tools[tool]);
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white via-blue-50 to-blue-100">
      {/* Main Content Section */}
      <div className="container mx-auto px-4 py-12">
        {/* Header Section with Animation */}
        <div className="text-center mb-12 animate-fadeIn">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('safetyPro.title')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('safetyPro.subtitle')}
          </p>
        </div>

        <Breadcrumbs />

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Inertization Calculation Card */}
          <div 
            onClick={() => navigateToTool('inertization')}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group"
          >
            <div className="p-8 text-center relative">
              {/* Icon Background with Animation */}
              <div className="w-20 h-20 bg-blue-50 rounded-full mx-auto flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-calculator text-3xl text-blue-500 group-hover:rotate-12 transition-transform duration-300"></i>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 group-hover:text-blue-600 transition-colors duration-300">
                {t('safetyPro.inertizationCalc')}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                {t('safetyPro.inertizationDesc')}
              </p>
              
              {/* Features List */}
              {/* <div className="text-left mt-6 border-t pt-4">
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                    Precise gas volume calculations
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                    Safety margin optimization
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                    Real-time adjustments
                  </li>
                </ul>
              </div> */}

              {/* Action Button */}
              <button className="mt-6 px-6 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-medium hover:bg-blue-100 transition-colors duration-300">
                {t('safetyPro.calculateNow')}
              </button>
            </div>
          </div>

          {/* Ventilation Calculation Card */}
          <div 
            onClick={() => navigateToTool('ventilation')}
            className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group"
          >
            <div className="p-8 text-center relative">
              {/* Icon Background with Animation */}
              <div className="w-20 h-20 bg-green-50 rounded-full mx-auto flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-wind text-3xl text-green-500 group-hover:rotate-12 transition-transform duration-300"></i>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3 group-hover:text-green-600 transition-colors duration-300">
                {t('safetyPro.ventilationCalc')}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                {t('safetyPro.ventilationDesc')}
              </p>
              
              {/* Features List */}
              {/* <div className="text-left mt-6 border-t pt-4">
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                    Air flow rate optimization
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-check-circle text-green-500 mr-2"></i>
                    Hazard level assessment
                  </li>
                </ul>
              </div> */}

              {/* Action Button */}
              <button className="mt-6 px-6 py-2 bg-green-50 text-green-600 rounded-full text-sm font-medium hover:bg-green-100 transition-colors duration-300">
                Calculate Now
              </button>
            </div>
          </div>

          {/* Coming Soon Card */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10"></div>
            <div className="p-8 text-center relative">
              <div className="w-20 h-20 bg-purple-50 rounded-full mx-auto flex items-center justify-center mb-6">
                <i className="fas fa-flask text-3xl text-purple-500"></i>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">
                {t('safetyPro.moreToolsComingSoon')}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                {t('safetyPro.moreToolsDesc')}
              </p>
              
              {/* Feature Preview */}
              {/* <div className="text-left mt-6 border-t pt-4">
                <ul className="space-y-2">
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-clock text-purple-500 mr-2"></i>
                    Pressure Safety Calculations
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-clock text-purple-500 mr-2"></i>
                    Temperature Control Analysis
                  </li>
                  <li className="flex items-center text-sm text-gray-600">
                    <i className="fas fa-clock text-purple-500 mr-2"></i>
                    Chemical Compatibility Check
                  </li>
                </ul>
              </div> */}

              {/* Notification Button */}
              <button className="mt-6 px-6 py-2 bg-purple-50 text-purple-600 rounded-full text-sm font-medium hover:bg-purple-100 transition-colors duration-300">
                {t('safetyPro.getNotified')}
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Info Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('safetyPro.bottomInfo')}
          </p>
          
          {/* Support Contact */}
          <div className="mt-8 inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors duration-300">
            <i className="fas fa-headset text-xl"></i>
            <span>{t('safetyPro.supportContact')}</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* Animations */
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-in-out;
        }

        .animate-bounce {
          animation: bounce 2s infinite;
        }

        .animate-pulse {
          animation: pulse 2s infinite;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-3px);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        /* Group hover animations */
        .group:hover .group-hover\\:rotate-12 {
          transform: rotate(12deg);
        }

        .group:hover .group-hover\\:scale-110 {
          transform: scale(1.1);
        }

        /* Gradient animation */
        @keyframes gradientFlow {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .bg-gradient-animate {
          background-size: 200% 200%;
          animation: gradientFlow 3s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default SafetyPro;
